import { useEffect, useMemo, useRef, useState } from 'react';

import { LANGUAGE } from '@/i18n/dictionary';
import { useTranslation } from '@/i18n/useTranslation';
import { useKioskSession } from '@/lib/kioskSession';
import type { CalendarMonth } from '@/shared/calendar';
import { addMonths, currentMonth, monthGrid, monthOf, todayIso } from '@/shared/calendar';

export interface DateCalendarProps {
  /** ISO `YYYY-MM-DD`, or empty. Decides which month opens and which day reads as selected. */
  readonly value: string;
  readonly onSelect: (iso: string) => void;
  readonly onClose: () => void;
  /** Labels the dialog with the field it belongs to, so it is announced as that field's calendar. */
  readonly labelledBy: string;
  /**
   * The control that opened the calendar, exempt from the outside-press close.
   *
   * ⚠️ Without it the calendar could not be dismissed by the button that opened it. `pointerdown` fires
   * before `click`, so the press closed the dialog and the click that followed toggled it straight back
   * open — on a kiosk, where a finger is the only input, the toggle's "Close the calendar" label was a
   * control that did nothing.
   */
  readonly ignore?: React.RefObject<HTMLElement | null>;
  /**
   * The earliest day that can be CHOSEN, ISO `YYYY-MM-DD`. Earlier days render disabled. Empty is no floor.
   *
   * Compared as a string, not as a date: `YYYY-MM-DD` orders lexicographically exactly as it orders
   * chronologically, so the floor needs no arithmetic and cannot re-acquire the timezone bug `todayIso`
   * documents. Empty blocks nothing, because no ISO date sorts before `''`.
   */
  readonly min?: string;
}

/**
 * The one place a day's colour is decided, because three of its states overlap.
 *
 * Written as a single choice rather than as stacked conditional classes: `selected` and `blocked` both set
 * the text colour, and Tailwind resolves two arbitrary `text-[…]` utilities by stylesheet order rather than
 * by the order they appear in the attribute — so a held STALE date (selected and blocked at once, which is
 * exactly what a returning associate's record produces) would have taken whichever of the two happened to
 * be emitted later. Selected wins here on purpose: the candidate must be able to see the date they hold.
 *
 * ⚠️ No frame draws the open calendar (§ 8 — the native popover it replaces had none to draw), so the
 * blocked day reuses the token the out-of-month padding days already use rather than inventing a value.
 * Both mean the same thing to the candidate: not a day you are picking here.
 */
function dayTone({
  blocked,
  inMonth,
  selected,
}: {
  readonly blocked: boolean;
  readonly inMonth: boolean;
  readonly selected: boolean;
}): string {
  if (selected) return 'bg-[var(--primary)] font-semibold text-[var(--primary-foreground)]';
  if (blocked || !inMonth) return 'text-[var(--subtle-foreground)]';
  return 'text-[var(--foreground)]';
}

/**
 * The month grid behind the availability date [decision 0180].
 *
 * **A day before `min` cannot be chosen, but a value already held is still shown.** Those are two halves of
 * one ruling and they pull in opposite directions, so both are stated here: a candidate answering when they
 * can start is choosing today or a later day, and Andrea Loretti ruled the past out for a new answer — but a
 * returning associate's record already holds an old availability date, and nothing may block them on a field
 * they never touched. Nicolás Moreno's distinction is the one the code implements: *you should not pick an
 * old date, but you can have an old date*.
 *
 * So the grid DISABLES everything before `min`, and still opens on the value's own month and still draws
 * that value as selected — a returning associate sees the date they hold rather than being stranded a page
 * away from it, and cannot silently re-answer with another stale one.
 *
 * ⚠️ **The floor stops what is OFFERED here and nothing else.** It is not validation: the screens accept a
 * past date and so does the api, deliberately (`min` is advisory — see `DateField` and decision 0180).
 *
 * It is hand-built because the native `<input type="date">` had to go: its displayed order comes from the
 * browser's locale and no attribute overrides it, which is what `DateField` exists to fix. Losing the native
 * popover was the cost of that, and this is it paid back — including the `min` the native control carried.
 */
export function DateCalendar({
  value,
  onSelect,
  onClose,
  labelledBy,
  ignore,
  min = '',
}: DateCalendarProps) {
  const t = useTranslation();
  const language = useKioskSession((state) => state.language);
  const [month, setMonth] = useState<CalendarMonth>(() => monthOf(value, currentMonth()));
  const container = useRef<HTMLDivElement>(null);

  const today = todayIso();
  const days = useMemo(() => monthGrid(month), [month]);

  /*
   * Month and weekday names come from the platform in the selected language rather than from the dictionary.
   * Twelve months and seven weekdays in two languages is thirty-eight strings that `Intl` already has right,
   * including the capitalisation Spanish uses for them, which differs from English.
   */
  const locale = language === LANGUAGE.es ? 'es' : 'en-US';
  const heading = useMemo(
    () =>
      new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(
        new Date(Date.UTC(month.year, month.month - 1, 1)),
      ),
    [locale, month],
  );
  const weekdays = useMemo(() => {
    const short = new Intl.DateTimeFormat(locale, { weekday: 'short', timeZone: 'UTC' });
    const long = new Intl.DateTimeFormat(locale, { weekday: 'long', timeZone: 'UTC' });
    // 4 January 1970 was a Sunday, which is the column this grid starts on.
    return [0, 1, 2, 3, 4, 5, 6].map((offset) => {
      const date = new Date(Date.UTC(1970, 0, 4 + offset));
      return { short: short.format(date), long: long.format(date) };
    });
  }, [locale]);
  const dayLabel = useMemo(
    () => new Intl.DateTimeFormat(locale, { dateStyle: 'full', timeZone: 'UTC' }),
    [locale],
  );

  /*
   * Escape closes it, and a tap outside closes it. Both are what a person expects of an overlay, and on a
   * kiosk the second matters more than usual — there is no window chrome to click away to, so a calendar
   * that could only be dismissed by its own button would be a trap for anyone who opened it by accident.
   */
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (container.current?.contains(target) !== false) return;
      // The trigger closes the calendar through its own handler; closing here too would re-open it.
      if (ignore?.current?.contains(target) === true) return;
      onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [onClose, ignore]);

  return (
    <div
      aria-labelledby={labelledBy}
      aria-modal="false"
      className="absolute left-0 top-full z-10 mt-1 w-[19.5rem] rounded-[var(--radius-sm)] bg-[var(--card)] p-3 shadow-lg ring-1 ring-[var(--border-subtle)]"
      ref={container}
      role="dialog"
    >
      <div className="flex items-center justify-between gap-2">
        <button
          aria-label={t('calendar.previousMonth')}
          className="kiosk-focusable flex h-11 w-11 items-center justify-center rounded-[var(--radius-xs)] text-xl text-[var(--foreground)]"
          onClick={() => {
            setMonth((current) => addMonths(current, -1));
          }}
          type="button"
        >
          ‹
        </button>
        {/* `aria-live` so paging months is announced — otherwise a screen-reader user pages blind. */}
        <span aria-live="polite" className="text-base font-semibold text-[var(--foreground)]">
          {heading}
        </span>
        <button
          aria-label={t('calendar.nextMonth')}
          className="kiosk-focusable flex h-11 w-11 items-center justify-center rounded-[var(--radius-xs)] text-xl text-[var(--foreground)]"
          onClick={() => {
            setMonth((current) => addMonths(current, 1));
          }}
          type="button"
        >
          ›
        </button>
      </div>

      <div aria-hidden="true" className="mt-2 grid grid-cols-7 gap-1">
        {weekdays.map((weekday) => (
          <span
            className="flex h-6 items-center justify-center text-xs font-semibold uppercase text-[var(--subtle-foreground)]"
            key={weekday.long}
          >
            {weekday.short.slice(0, 2)}
          </span>
        ))}
      </div>

      <div className="mt-1 grid grid-cols-7 gap-1">
        {days.map((day) => {
          const selected = day.iso === value;
          const isToday = day.iso === today;
          const blocked = day.iso < min;
          return (
            <button
              aria-current={isToday ? 'date' : undefined}
              // The full date, not the number: "15" tells a screen-reader user nothing about which month
              // they are in after paging.
              aria-label={dayLabel.format(new Date(`${day.iso}T00:00:00Z`))}
              aria-pressed={selected}
              className={`kiosk-focusable flex h-11 w-11 items-center justify-center rounded-[var(--radius-xs)] text-base ${dayTone(
                { blocked, inMonth: day.inMonth, selected },
              )} ${isToday && !selected ? 'ring-1 ring-[var(--border-subtle)]' : ''} ${
                blocked ? 'cursor-not-allowed' : ''
              }`}
              /*
               * Native `disabled`, not `aria-disabled` (§ 9 native semantics first): it refuses the tap
               * without a handler that has to remember to, it is announced as unavailable, and it drops the
               * blocked days out of the tab order — on a six-row grid that is up to 42 tab stops, and the
               * ones removed are exactly the days nobody can use.
               */
              disabled={blocked}
              key={day.iso}
              onClick={() => {
                onSelect(day.iso);
              }}
              type="button"
            >
              {day.day}
            </button>
          );
        })}
      </div>
    </div>
  );
}
